import React, { useContext, useMemo } from 'react';
import { ItemSelectedForZoomRegionContext } from '../context-provider';
import {
  getZoomRectAt,
  getZoomWrapperStyle,
  IDENTITY_ZOOM_RECT,
  type ZoomKeyframe,
} from './animation/zoom-keyframes';

/**
 * 프레임 고정 내부 줌 래퍼 — croppable layer 의 outer(overflow hidden) 바로 아래에
 * 끼워 현재 프레임의 보간 rect 를 scale+translate 로 적용한다(zoom-keyframes.ts).
 *
 * 키프레임이 없으면 children 을 그대로 반환해 기존 DOM 구조를 유지한다. 래퍼
 * 장착/해제는 키프레임 존재 여부 단위라 재생 중에는 구조가 바뀌지 않는다(비디오
 * 재마운트 방지) — rect 가 일시적으로 identity 여도 래퍼는 유지된다.
 *
 * 줌 영역 편집 중(itemSelectedForZoomRegion === itemId)에는 identity 로 렌더한다 —
 * 오버레이의 영역 rect 는 원본 콘텐츠 좌표 기준이므로, 줌이 적용된 화면 위에
 * 그리면 편집 좌표와 보이는 콘텐츠가 어긋난다(crop 편집이 원본을 보여주는 것과
 * 같은 규약). 렌더 서비스에선 context 기본값 null 이라 항상 줌이 적용된다.
 */
export const ZoomWrapper: React.FC<{
  itemId?: string;
  zoomKeyframes: ZoomKeyframe[] | undefined;
  frame: number;
  children: React.ReactNode;
}> = ({ itemId, zoomKeyframes, frame, children }) => {
  const editingItemId = useContext(ItemSelectedForZoomRegionContext);
  const isEditingRegion = itemId !== undefined && itemId === editingItemId;

  const style = useMemo(
    () =>
      zoomKeyframes && zoomKeyframes.length > 0
        ? getZoomWrapperStyle(
            isEditingRegion
              ? IDENTITY_ZOOM_RECT
              : getZoomRectAt(zoomKeyframes, frame),
          )
        : undefined,
    [zoomKeyframes, frame, isEditingRegion],
  );

  if (!style) {
    return <>{children}</>;
  }

  return <div style={style}>{children}</div>;
};
